import React from 'react';
import { useProgress } from '../../hooks/useDashboard';
import { useTranslation, formatNumber, formatDate } from '../../../../shared/i18n';

const renderProgressBar = (achieved, target) => {
  const percentage = target > 0 ? Math.min((achieved / target) * 100, 100) : 0;
  const isComplete = achieved >= target && target > 0;

  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: '120px' }}>
      <div
        className="bg-secondary bg-opacity-25 rounded-pill flex-grow-1"
        style={{ height: '8px', position: 'relative' }}
      >
        <div
          className={`rounded-pill ${isComplete ? 'bg-success' : 'bg-primary'}`}
          style={{
            width: `${percentage}%`,
            height: '8px',
            position: 'absolute',
            top: '0',
            left: '0',
            transition: 'width 0.3s ease'
          }}
        />
      </div>
      {isComplete && (
        <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '14px' }}></i>
      )}
    </div>
  );
};

const calculateSummary = (progress) => {
  if (!progress || progress.length === 0) return { totalAchieved: 0, totalTarget: 0, daysCompleted: 0 };

  return progress.reduce((acc, day) => ({
    totalAchieved: acc.totalAchieved + day.achieved_xp,
    totalTarget: acc.totalTarget + day.target_xp,
    daysCompleted: acc.daysCompleted + (day.achieved_xp >= day.target_xp && day.target_xp > 0 ? 1 : 0)
  }), { totalAchieved: 0, totalTarget: 0, daysCompleted: 0 });
};

export const Progress = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useProgress();

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <header className="mb-4">
        <h1 className="fs-2 fw-bold mb-1">{t('children.dashboard.sidebar.progress')}</h1>
        <p className="text-secondary mb-0">{t('dashboard.index.children.progress_modal.subtitle')}</p>
      </header>

      {isLoading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          {t('common.error')}: {error.message}
        </div>
      )}

      {data && (
        <>
          {/* Summary Stats */}
          {(() => {
            const summary = calculateSummary(data.progress);
            return (
              <div className="row g-3 mb-4">
                <div className="col-4">
                  <div className="card border-0 rounded-4 h-100">
                    <div className="card-body p-3 text-center">
                      <div className="fw-bold text-primary" style={{ fontSize: '24px' }}>
                        {formatNumber(summary.totalAchieved)}
                      </div>
                      <small className="text-muted">{t('dashboard.index.children.progress_modal.total_xp')}</small>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="card border-0 rounded-4 h-100">
                    <div className="card-body p-3 text-center">
                      <div className="fw-bold text-success" style={{ fontSize: '24px' }}>
                        {summary.daysCompleted}
                      </div>
                      <small className="text-muted">{t('dashboard.index.children.progress_modal.days_completed')}</small>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="card border-0 rounded-4 h-100">
                    <div className="card-body p-3 text-center">
                      <div className="fw-bold text-info" style={{ fontSize: '24px' }}>
                        {summary.totalTarget > 0 ? Math.round((summary.totalAchieved / summary.totalTarget) * 100) : 0}%
                      </div>
                      <small className="text-muted">{t('dashboard.index.children.progress_modal.overall_progress')}</small>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Daily Progress Table */}
          <div className="card border-0 rounded-4">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-4">{t('dashboard.index.children.progress_modal.date')}</th>
                      <th className="text-end">{t('dashboard.index.children.progress_modal.target')}</th>
                      <th className="text-end">{t('dashboard.index.children.progress_modal.achieved')}</th>
                      <th className="pe-4" style={{ width: '150px' }}>{t('dashboard.index.children.progress_modal.progress')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.progress.slice().reverse().map((day) => (
                      <tr key={day.date}>
                        <td className="ps-4">
                          <span className="fw-medium">{formatDate(day.date, { day: 'numeric', month: 'short' })}</span>
                        </td>
                        <td className="text-end text-muted">
                          {formatNumber(day.target_xp)} XP
                        </td>
                        <td className="text-end">
                          <span className={day.achieved_xp >= day.target_xp && day.target_xp > 0 ? 'text-success fw-medium' : ''}>
                            {formatNumber(day.achieved_xp)} XP
                          </span>
                        </td>
                        <td className="pe-4">
                          {renderProgressBar(day.achieved_xp, day.target_xp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
